import React, { useState, useEffect, useRef } from 'react';
import { useNavigate, useParams, useLocation } from 'react-router-dom';
import { motion, AnimatePresence } from 'framer-motion';
import { ArrowLeft, Check } from 'lucide-react';
import DashboardLayout from '../../components/layout/DashboardLayout';
import Card from '../../components/ui/Card';
import Button from '../../components/ui/Button';
import Input from '../../components/ui/Input';
import ConfirmDialog from '../../components/ui/ConfirmDialog';
import { SessionNotFoundEmpty } from '../../components/ui/EmptyState';
import { FormSkeleton } from '../../components/ui/LoadingSkeleton';
import { notifications } from '../../utils/toast';
import { useKeyboardShortcut } from '../../hooks/useKeyboardShortcuts';
import { sessionService, clientService, api } from '../../services';
import { MAX_UPLOAD_MB } from '../../utils/uploadLimits';

/**
 * Edit Session Page - 3 Step Wizard
 * Edit existing session details
 */

const EditSessionPage = () => {
  const navigate = useNavigate();
  const { sessionId } = useParams();
  const location = useLocation();

  /**
   * This page is mounted under both /client and /trainer. Every internal
   * navigation is built from basePath so a trainer editing a session is not
   * bounced into the client panel (which their role cannot open).
   *
   * Only SOLO trainers get here: an org-employed trainer delivers someone
   * else's session, and changing its date or capacity is the client's call
   * (same rule as reschedule in TrainerSessionsPage). The entry buttons are
   * already gated, and this redirect covers a hand-typed URL.
   */
  const isTrainerView = location.pathname.startsWith('/trainer');
  const basePath = isTrainerView ? '/trainer' : '/client';

  useEffect(() => {
    if (!isTrainerView) return;
    try {
      const u = JSON.parse(localStorage.getItem('user') || '{}');
      const org = u.primaryOrganization || u.organization;
      if (org?.type !== 'individual') navigate('/trainer/sessions', { replace: true });
    } catch {
      navigate('/trainer/sessions', { replace: true });
    }
    // eslint-disable-next-line react-hooks/exhaustive-deps
  }, [isTrainerView]);
  const [currentStep, setCurrentStep] = useState(1);
  const [user, setUser] = useState(null);
  /**
   * Effective per-session participant cap from the API. An admin can raise a
   * single org above its plan default (Organization.limits.maxParticipantsOverride),
   * and the backend already honours that everywhere — but this page derived the cap
   * from a hardcoded plan map, so an org granted 75 was still told "Fixed at 30"
   * and blocked at 30. null until loaded, then it replaces the plan-map value.
   */
  const [effectiveLimit, setEffectiveLimit] = useState(null);
  const [session, setSession] = useState(null);
  const [trainers, setTrainers] = useState([]);
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);

  const [formData, setFormData] = useState({
    // Step 1: Basic Info
    name: '',
    description: '',
    startDate: '',
    endDate: '',
    startTime: '',
    endTime: '',
    timezone: 'Asia/Kolkata',

    // Step 2: Trainer & Participants
    trainerId: '',
    expectedParticipants: 30,
    maxParticipants: 30,
    sessionType: 'public',
    enableOverallFeedback: true, // Overall session feedback enabled by default

    // Step 3: Workbooks
    workbooks: [],
    instructions: '',

    // Step 4 (Phase 2): Pre-Session
    preSessionEnabled: false,
    preSessionMaterials: [],
    selfAssessmentEnabled: false,
    selfAssessmentQuestions: [],
    challengeCaptureEnabled: true,
    expectationCaptureEnabled: true,

    // Step 4 (Phase 2): Post-Session
    postSessionEnabled: false,
    actionPlanTrackingEnabled: true,
    managerReinforcementEnabled: true,
    reflectionCyclesEnabled: true,
    reflectionIntervals: [30, 60, 90],
    reflectionQuestions: [],
  });

  /**
   * Raw text for the reflection-intervals field, held separately from the parsed array.
   *
   * The input used to render `reflectionIntervals.join(', ')` and reparse on every
   * keystroke, so a half-typed value was destroyed as it was typed: "30," split to
   * ["30",""], the empty half became NaN, the filter dropped it, and join() gave back
   * "30" — deleting the comma on the same keypress that entered it. A second interval
   * could never be added.
   *
   * The array is still updated live so saving needs no extra wiring; this only stops the
   * parsed value being echoed back into the box mid-edit.
   */
  const [reflectionIntervalsText, setReflectionIntervalsText] = useState('30, 60, 90');

  // Separate state for 12-hour time format with AM/PM
  const [timeData, setTimeData] = useState({
    startHour: '09',
    startMinute: '00',
    startPeriod: 'AM',
    endHour: '10',
    endMinute: '00',
    endPeriod: 'AM',
  });

  const [errors, setErrors] = useState({});

  // Track if form has been modified (dirty state)
  const [isDirty, setIsDirty] = useState(false);
  const [showBackConfirm, setShowBackConfirm] = useState(false);
  const [pendingNavigation, setPendingNavigation] = useState(null);
  const initialFormDataRef = useRef(null);

  // Convert 24-hour to 12-hour format
  const convertTo12Hour = (time24) => {
    if (!time24) return { hour: '09', minute: '00', period: 'AM' };
    const [hours, minutes] = time24.split(':');
    let h = parseInt(hours, 10);
    const period = h >= 12 ? 'PM' : 'AM';
    if (h === 0) h = 12;
    else if (h > 12) h = h - 12;
    return {
      hour: h.toString().padStart(2, '0'),
      minute: minutes || '00',
      period,
    };
  };

  // Convert 12-hour to 24-hour format
  const convertTo24Hour = (hour, minute, period) => {
    let h = parseInt(hour, 10);
    if (period === 'AM') {
      if (h === 12) h = 0;
    } else {
      if (h !== 12) h = h + 12;
    }
    return `${h.toString().padStart(2, '0')}:${minute}`;
  };

  // Create ISO datetime string from date and time in specified timezone
  // The time entered is in the SELECTED timezone, we need to convert to UTC
  const createISODateTime = (dateStr, timeStr, tz) => {
    if (!dateStr || !timeStr) return null;
    try {
      const [hours, minutes] = timeStr.split(':').map(Number);
      const [year, month, day] = dateStr.split('-').map(Number);

      // Get timezone offset by comparing formatted time in target TZ vs UTC
      // Create a reference date in UTC
      const utcDate = new Date(Date.UTC(year, month - 1, day, hours, minutes, 0));

      // Get what this UTC time looks like in the target timezone
      const tzParts = new Intl.DateTimeFormat('en-US', {
        timeZone: tz,
        year: 'numeric',
        month: '2-digit',
        day: '2-digit',
        hour: '2-digit',
        minute: '2-digit',
        hour12: false,
      }).formatToParts(utcDate);

      const getPart = (type) => {
        const part = tzParts.find((p) => p.type === type);
        return part ? parseInt(part.value, 10) : 0;
      };

      const tzHour = getPart('hour');
      const tzMinute = getPart('minute');
      const tzDay = getPart('day');
      const tzMonth = getPart('month');
      const tzYear = getPart('year');

      // Calculate the difference between what user entered and what TZ shows
      // User entered 12:15 in TZ X, but utcDate formatted in TZ X shows different time
      // The difference tells us the offset
      const userEnteredMs = new Date(year, month - 1, day, hours, minutes, 0).getTime();
      const tzDisplayMs = new Date(tzYear, tzMonth - 1, tzDay, tzHour, tzMinute, 0).getTime();
      const offsetMs = userEnteredMs - tzDisplayMs;

      // Apply offset to get correct UTC time
      const correctUtc = new Date(utcDate.getTime() + offsetMs);
      return correctUtc.toISOString();
    } catch (e) {
      console.error('Error creating ISO datetime:', e);
      // Fallback: just combine date and time (treating as local)
      return `${dateStr}T${timeStr}:00.000Z`;
    }
  };

  // Generate hour options (01-12)
  const hourOptions = Array.from({ length: 12 }, (_, i) => {
    const hour = i + 1;
    return hour.toString().padStart(2, '0');
  });

  // Generate minute options (00, 05, 10, ... 55) - 5 minute intervals for easier selection
  const minuteOptions = Array.from({ length: 12 }, (_, i) => (i * 5).toString().padStart(2, '0'));

  useEffect(() => {
    // Get user from localStorage
    const userData = localStorage.getItem('user');
    if (userData) {
      setUser(JSON.parse(userData));
    }

    const loadData = async () => {
      try {
        // Load trainers and session in parallel
        const [trainersResponse, sessionResponse] = await Promise.all([
          clientService.getTrainers(),
          sessionService.getSession(sessionId),
        ]);

        // Set trainers - API returns { data: { trainers: [...] } }
        if (trainersResponse?.success && trainersResponse?.data) {
          const trainersArray = trainersResponse.data.trainers || trainersResponse.data;
          if (Array.isArray(trainersArray)) {
            // Only show active trainers in the dropdown
            const trainersList = trainersArray
              .filter((t) => t.trainerStatus !== 'inactive')
              .map((t) => ({
                id: t._id,
                name: `${t.firstName} ${t.lastName}`,
                fullName: `${t.firstName} ${t.lastName}`,
                email: t.email,
                expertise: t.expertise || [],
                yearsOfExperience: t.yearsOfExperience || 0,
                avgRating: t.avgRating || 0,
                totalSessions: t.sessionsCreated || 0,
              }));
            setTrainers(trainersList);
          }
        }

        // Set session data
        if (sessionResponse?.success && sessionResponse?.data) {
          const foundSession = sessionResponse.data;
          setSession(foundSession);

          // Parse date and time from available fields
          let date = '';
          let startTime = '';
          let endTime = '';

          // Get session timezone (or default to Asia/Kolkata)
          const sessionTimezone = foundSession.timezone || 'Asia/Kolkata';

          // Helper function to extract time from datetime stored as pseudo-UTC
          // Backend stores local time with Z suffix, so we read UTC hours/minutes directly
          const formatTimeInTimezone = (dateObj, tz) => {
            try {
              // Since backend stores time as pseudo-UTC (local time with Z suffix),
              // we extract the UTC hours/minutes directly
              const hours = dateObj.getUTCHours().toString().padStart(2, '0');
              const minutes = dateObj.getUTCMinutes().toString().padStart(2, '0');
              return `${hours}:${minutes}`;
            } catch (e) {
              // Fallback to local time if something goes wrong
              return dateObj.toTimeString().slice(0, 5);
            }
          };

          // Helper function to format date from datetime stored as pseudo-UTC
          // Backend stores local date with Z suffix, so we read UTC date directly
          const formatDateInTimezone = (dateObj, tz) => {
            try {
              // Since backend stores date as pseudo-UTC (local date with Z suffix),
              // we extract the UTC date directly
              const year = dateObj.getUTCFullYear();
              const month = (dateObj.getUTCMonth() + 1).toString().padStart(2, '0');
              const day = dateObj.getUTCDate().toString().padStart(2, '0');
              return `${year}-${month}-${day}`;
            } catch (e) {
              // Fallback to ISO date
              return dateObj.toISOString().split('T')[0];
            }
          };

          // Try to use startTime/endTime if they exist
          if (foundSession.startTime && foundSession.endTime) {
            const startDate = new Date(foundSession.startTime);
            const endDate = new Date(foundSession.endTime);

            // Check if dates are valid
            if (!isNaN(startDate.getTime()) && !isNaN(endDate.getTime())) {
              // Format date and times in the session's timezone
              date = formatDateInTimezone(startDate, sessionTimezone);
              startTime = formatTimeInTimezone(startDate, sessionTimezone);
              endTime = formatTimeInTimezone(endDate, sessionTimezone);
            }
          }

          // Fallback to scheduledDate if startTime/endTime don't exist
          if (!date && foundSession.scheduledDate) {
            const schedDate = new Date(foundSession.scheduledDate);
            if (!isNaN(schedDate.getTime())) {
              date = schedDate.toISOString().split('T')[0];
            }
          }

          // Get start date (prefer startDate field, fall back to scheduledDate or extracted from startTime)
          let startDate = '';
          if (foundSession.startDate) {
            const sDate = new Date(foundSession.startDate);
            if (!isNaN(sDate.getTime())) {
              startDate = formatDateInTimezone(sDate, sessionTimezone);
            }
          } else if (foundSession.scheduledDate) {
            const sDate = new Date(foundSession.scheduledDate);
            if (!isNaN(sDate.getTime())) {
              startDate = formatDateInTimezone(sDate, sessionTimezone);
            }
          } else {
            startDate = date; // Fallback to date extracted from startTime
          }

          // Get end date - extract from endTime or use endDate field if exists
          let endDateVal = '';
          if (foundSession.endDate) {
            const eDate = new Date(foundSession.endDate);
            if (!isNaN(eDate.getTime())) {
              endDateVal = formatDateInTimezone(eDate, sessionTimezone);
            }
          } else if (foundSession.endTime) {
            // Extract end date from endTime datetime field
            const eDate = new Date(foundSession.endTime);
            if (!isNaN(eDate.getTime())) {
              const extractedEndDate = formatDateInTimezone(eDate, sessionTimezone);
              // Only set endDate if it's different from startDate (multi-day session)
              if (extractedEndDate !== startDate) {
                endDateVal = extractedEndDate;
              }
            }
          }

          // Transform workbooks to have consistent structure
          console.log('[EditSession] Loaded workbooks from API:', foundSession.workbooks);
          const transformedWorkbooks = (foundSession.workbooks || []).map((wb) => ({
            id: wb._id || wb.id || `wb-${Date.now()}-${Math.random().toString(36).substr(2, 9)}`,
            _id: wb._id,
            name: wb.name || 'Untitled Workbook',
            url: wb.url,
            type: wb.type || 'unknown',
            size: wb.size || 0,
            uploadedAt: wb.uploadedAt,
            isNew: false, // Existing workbooks are not new
          }));
          console.log('[EditSession] Transformed workbooks:', transformedWorkbooks);

          setFormData({
            name: foundSession.name,
            description: foundSession.description || '',
            startDate: startDate,
            endDate: endDateVal,
            startTime: startTime,
            endTime: endTime,
            timezone: foundSession.timezone || 'Asia/Kolkata',
            trainerId: foundSession.trainer?._id || foundSession.trainer || '',
            expectedParticipants: foundSession.expectedParticipants || foundSession.maxParticipants || 30,
            // maxParticipants is not user-editable — always reflects the current plan limit.
            maxParticipants: (() => {
              const limit = getParticipantLimit();
              return limit === -1 ? 500 : limit;
            })(),
            sessionType: foundSession.sessionType || 'public',
            enableOverallFeedback: foundSession.overallFeedback?.enabled !== false, // Default to true
            workbooks: transformedWorkbooks,
            instructions: foundSession.instructions || '',

            // Phase 2: pre-session (safe defaults — Phase 1 sessions have these undefined)
            preSessionEnabled: foundSession.preSession?.enabled || false,
            preSessionMaterials: (foundSession.preSession?.materials || []).map(m => ({
              _id: m._id,
              name: m.name,
              url: m.url,
              type: m.type,
              size: m.size,
              uploadedAt: m.uploadedAt,
            })),
            selfAssessmentEnabled: foundSession.preSession?.selfAssessment?.enabled || false,
            // Seed `optionsText` from stored options so the MCQ input has the right
            // initial display string (the UI reads optionsText when defined).
            selfAssessmentQuestions: (foundSession.preSession?.selfAssessment?.questions || []).map(q => ({
              ...q,
              optionsText: (q.options || []).join(', '),
            })),
            challengeCaptureEnabled: foundSession.preSession?.challengeCapture?.enabled !== false,
            expectationCaptureEnabled: foundSession.preSession?.expectationCapture?.enabled !== false,

            // Phase 2: post-session
            // Manager reinforcement and reflection cycles default to ON (use `!== false` so
            // missing fields load as enabled; only an explicit stored `false` turns them off).
            postSessionEnabled: foundSession.postSession?.enabled || false,
            actionPlanTrackingEnabled: foundSession.postSession?.actionPlanTracking?.enabled !== false,
            managerReinforcementEnabled: foundSession.postSession?.managerReinforcement?.enabled !== false,
            reflectionCyclesEnabled: foundSession.postSession?.reflectionCycles?.enabled !== false,
            reflectionIntervals: foundSession.postSession?.reflectionCycles?.intervals || [30, 60, 90],
            reflectionQuestions: foundSession.postSession?.reflectionCycles?.questions || [],
          });

          // Seed the editable text from the saved intervals — it is only decoupled from
          // the array while the user is typing, not on load.
          setReflectionIntervalsText(
            (foundSession.postSession?.reflectionCycles?.intervals || [30, 60, 90]).join(', ')
          );

          // Convert loaded times to 12-hour format for time picker
          const startParsed = convertTo12Hour(startTime);
          const endParsed = convertTo12Hour(endTime);
          setTimeData({
            startHour: startParsed.hour,
            startMinute: startParsed.minute,
            startPeriod: startParsed.period,
            endHour: endParsed.hour,
            endMinute: endParsed.minute,
            endPeriod: endParsed.period,
          });

          // Store initial form data for dirty checking
          initialFormDataRef.current = JSON.stringify({
            name: foundSession.name,
            description: foundSession.description || '',
            startDate: startDate,
            endDate: endDateVal,
            startTime: startTime,
            endTime: endTime,
            timezone: foundSession.timezone || 'Asia/Kolkata',
            trainerId: foundSession.trainer?._id || foundSession.trainer || '',
            expectedParticipants: foundSession.expectedParticipants || foundSession.maxParticipants || 30,
            maxParticipants: (() => {
              const limit = getParticipantLimit();
              return limit === -1 ? 500 : limit;
            })(),
            sessionType: foundSession.sessionType || 'public',
            workbooks: transformedWorkbooks.map(w => ({ id: w.id, name: w.name })),
            instructions: foundSession.instructions || '',
          });
        }
      } catch (error) {
        console.error('Error loading session data:', error);
        notifications.error('Failed to load session data');
      } finally {
        setLoading(false);
      }
    };

    loadData();
  }, [sessionId]);

  // Keyboard shortcuts
  useKeyboardShortcut(['ctrl+s', 'cmd+s'], () => {
    if (!saving && session) {
      handleSaveSession();
    }
  });

  useKeyboardShortcut(['escape', 'esc'], () => {
    handleBackNavigation(`${basePath}/sessions/${sessionId}`);
  });

  // Check if form has been modified
  const checkIfDirty = () => {
    if (!initialFormDataRef.current) return false;
    const currentData = JSON.stringify({
      name: formData.name,
      description: formData.description,
      startDate: formData.startDate,
      endDate: formData.endDate,
      startTime: formData.startTime,
      endTime: formData.endTime,
      timezone: formData.timezone,
      trainerId: formData.trainerId,
      expectedParticipants: formData.expectedParticipants,
      maxParticipants: isUnlimitedParticipants() ? 500 : getParticipantLimit(),
      sessionType: formData.sessionType,
      workbooks: formData.workbooks.map(w => ({ id: w.id, name: w.name })),
      instructions: formData.instructions,
    });
    return currentData !== initialFormDataRef.current;
  };

  // Handle back navigation with unsaved changes check
  const handleBackNavigation = (path) => {
    if (checkIfDirty()) {
      setPendingNavigation(path);
      setShowBackConfirm(true);
    } else {
      navigate(path);
    }
  };

  // Confirm leaving without saving
  const confirmLeave = () => {
    setShowBackConfirm(false);
    if (pendingNavigation) {
      navigate(pendingNavigation);
    }
  };

  const handleChange = (e) => {
    const { name, value } = e.target;

    // Clamp participant fields to plan limits
    if (name === 'maxParticipants') {
      const limit = getParticipantLimit();
      const num = Number(value);
      if (limit !== -1 && num > limit) {
        setFormData((prev) => ({ ...prev, [name]: limit }));
        setIsDirty(true);
        setErrors((prev) => ({ ...prev, maxParticipants: `Maximum ${limit} for your plan` }));
        return;
      }
    }
    if (name === 'expectedParticipants') {
      const limit = getParticipantLimit();
      // The cap is the authority. maxParticipants is display-only and mirrors it,
      // but a plan-map seeder can land AFTER the real cap resolves and leave 30 in
      // form state — clamping to that told users "Cannot exceed 30" next to a
      // field reading 75.
      const clampTo = limit === -1 ? 500 : limit;
      const num = Number(value);
      if (num > clampTo) {
        setFormData((prev) => ({ ...prev, [name]: clampTo }));
        setIsDirty(true);
        setErrors((prev) => ({ ...prev, expectedParticipants: `Cannot exceed ${clampTo}` }));
        return;
      }
    }

    setFormData((prev) => ({ ...prev, [name]: value }));
    setIsDirty(true);
    if (errors[name]) {
      setErrors((prev) => ({ ...prev, [name]: '' }));
    }
  };

  // Handle time changes for manual input (limit to 2 digits)
  const handleTimeChange = (field, value) => {
    // Limit input to 2 characters max for hour and minute fields
    const limitedValue = (field.includes('Hour') || field.includes('Minute'))
      ? value.toString().slice(0, 2)
      : value;
    const newTimeData = { ...timeData, [field]: limitedValue };
    setTimeData(newTimeData);
    setIsDirty(true);

    // Update formData with 24-hour format
    const startTime24 = convertTo24Hour(
      field.startsWith('start') && field.includes('Hour') ? limitedValue : newTimeData.startHour,
      field.startsWith('start') && field.includes('Minute') ? limitedValue : newTimeData.startMinute,
      field === 'startPeriod' ? limitedValue : newTimeData.startPeriod
    );
    const endTime24 = convertTo24Hour(
      field.startsWith('end') && field.includes('Hour') ? limitedValue : newTimeData.endHour,
      field.startsWith('end') && field.includes('Minute') ? limitedValue : newTimeData.endMinute,
      field === 'endPeriod' ? limitedValue : newTimeData.endPeriod
    );

    setFormData((prev) => ({
      ...prev,
      startTime: startTime24,
      endTime: endTime24,
    }));

    // Clear time errors
    if (errors.startTime || errors.endTime) {
      setErrors((prev) => ({ ...prev, startTime: '', endTime: '' }));
    }
  };

  // Handle time blur to validate and format the value
  const handleTimeBlur = (field, value) => {
    let formattedValue = value;

    if (field.includes('Hour')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 1) {
        formattedValue = '01';
      } else if (num > 12) {
        formattedValue = '12';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (field.includes('Minute')) {
      const num = parseInt(value, 10);
      if (isNaN(num) || num < 0) {
        formattedValue = '00';
      } else if (num > 59) {
        formattedValue = '59';
      } else {
        formattedValue = num.toString().padStart(2, '0');
      }
    }

    if (formattedValue !== value) {
      handleTimeChange(field, formattedValue);
    }
  };

  // Step 1 Validation
  const validateStep1 = () => {
    const newErrors = {};
    if (!formData.name) newErrors.name = 'Session name is required';
    if (!formData.startDate) newErrors.startDate = 'Start date is required';
    // Time fields are optional - end date is also optional

    // Check if start date is in the past
    if (formData.startDate) {
      const selectedDate = new Date(formData.startDate);
      const today = new Date();
      today.setHours(0, 0, 0, 0);
      selectedDate.setHours(0, 0, 0, 0);

      if (selectedDate < today) {
        newErrors.startDate = 'Cannot schedule session in the past';
      }

      // If start date is today, also check if start time is in the past
      if (formData.startTime && selectedDate.getTime() === today.getTime()) {
        const [hours, mins] = formData.startTime.split(':').map(Number);
        const now = new Date();
        const selectedDateTime = new Date();
        selectedDateTime.setHours(hours, mins, 0, 0);

        if (selectedDateTime < now) {
          newErrors.startTime = 'Start time has already passed';
        }
      }
    }

    // Validate end date if provided - must be on or after start date
    if (formData.endDate && formData.startDate) {
      const startDate = new Date(formData.startDate);
      const endDate = new Date(formData.endDate);
      startDate.setHours(0, 0, 0, 0);
      endDate.setHours(0, 0, 0, 0);

      if (endDate < startDate) {
        newErrors.endDate = 'End date cannot be before start date';
      }
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  // Resolve the real cap (plan default, or an admin override when one is set).
  useEffect(() => {
    clientService.getSubscription().then((res) => {
      const lim = res?.data?.features?.participants ?? res?.data?.usage?.participants?.limit;
      if (typeof lim === 'number' && lim !== 0) setEffectiveLimit(lim);
    }).catch(() => {});
  }, []);

  // maxParticipants is display-only and must track the resolved cap, which lands
  // after the session has already been loaded into formData.
  useEffect(() => {
    if (typeof effectiveLimit !== 'number' || effectiveLimit === 0) return;
    setFormData((prev) => ({ ...prev, maxParticipants: effectiveLimit === -1 ? 500 : effectiveLimit }));
  }, [effectiveLimit]);

  const getParticipantLimit = () => {
    // Prefer the API cap — it reflects any admin participant-cap override.
    if (typeof effectiveLimit === 'number' && effectiveLimit !== 0) return effectiveLimit;
    const participantLimits = { free: 30, pro: 30, pro_plus: 100, enterprise_starter: 200, enterprise_scale: -1 };
    const userPlan = user?.primaryOrganization?.plan || user?.organization?.plan || 'free';
    return participantLimits[userPlan] || 30;
  };

  const isUnlimitedParticipants = () => getParticipantLimit() === -1;

  // Step 2 Validation
  const validateStep2 = () => {
    const newErrors = {};
    if (!formData.trainerId) newErrors.trainerId = 'Please select a trainer';
    if (formData.expectedParticipants < 1) newErrors.expectedParticipants = 'Must be at least 1';
    const planLimit = getParticipantLimit();
    // Derive the cap here rather than reading formData.maxParticipants: an async
    // load effect can overwrite that with a stale plan-map value after the real
    // cap has resolved, which made a 50 under a 75 cap fail validation.
    const cap = planLimit === -1 ? 500 : planLimit;
    if (Number(formData.expectedParticipants) > cap) {
      newErrors.expectedParticipants = `Cannot exceed ${cap}`;
    }

    setErrors(newErrors);
    return Object.keys(newErrors).length === 0;
  };

  const handleNext = () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;
    setCurrentStep((prev) => prev + 1);
  };

  const handleBack = () => {
    setCurrentStep((prev) => prev - 1);
  };

  const handleFileUpload = (e) => {
    const files = Array.from(e.target.files);
    const tier = user?.organization?.plan || user?.subscriptionTier || 'free';
    const fileLimit = { free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[tier] || 2;

    if (formData.workbooks.length + files.length > fileLimit) {
      notifications.warning(`File limit reached. Maximum ${fileLimit} files allowed for your plan.`);
      return;
    }

    // Store files with File object for later upload
    const uploadedFiles = files.map((file) => ({
      id: Date.now() + Math.random(),
      name: file.name,
      size: file.size,
      type: file.type || file.name.split('.').pop() || 'unknown',
      previewUrl: URL.createObjectURL(file), // For local preview only
      file: file, // Store actual File object for upload
      isNew: true, // Mark as new file that needs upload
    }));

    setFormData((prev) => ({
      ...prev,
      workbooks: [...prev.workbooks, ...uploadedFiles],
    }));
    notifications.fileUploaded(files.length);
  };

  const removeWorkbook = (id) => {
    setFormData((prev) => ({
      ...prev,
      workbooks: prev.workbooks.filter((h) => h.id !== id),
    }));
    notifications.fileDeleted();
  };

  const handleSaveSession = async () => {
    if (currentStep === 1 && !validateStep1()) return;
    if (currentStep === 2 && !validateStep2()) return;

    setSaving(true);

    try {
      // Upload new files first
      const finalWorkbooks = [];
      for (const wb of formData.workbooks) {
        if (wb.isNew && wb.file) {
          // Upload new file to server
          try {
            console.log('[EditSession] Uploading new workbook:', wb.name);
            const uploadResult = await clientService.uploadWorkbook(sessionId, wb.file);
            console.log('[EditSession] Upload result:', uploadResult);
            if (uploadResult.success && uploadResult.data) {
              finalWorkbooks.push(uploadResult.data);
            } else {
              console.warn('[EditSession] Upload returned but no data:', uploadResult);
              notifications.warning(`Failed to upload ${wb.name}`);
            }
          } catch (uploadError) {
            console.error('[EditSession] Error uploading workbook:', wb.name, uploadError);
            notifications.error(`Failed to upload ${wb.name}: ${uploadError.message}`);
          }
        } else if (wb.url) {
          // Keep existing workbook (only if it has a valid URL)
          finalWorkbooks.push({
            _id: wb._id,
            name: wb.name,
            url: wb.url,
            type: wb.type,
            size: wb.size,
            uploadedAt: wb.uploadedAt || new Date().toISOString(),
          });
        }
      }

      console.log('[EditSession] Final workbooks to save:', finalWorkbooks.length);

      // Phase 2: split pre-read materials into already-uploaded vs newly-added,
      // upload new files to S3 via the pre-read endpoint, combine into one array.
      // Use api.upload (via api wrapper — handles baseURL + auth + 401).
      let finalPreReadMaterials = [];
      if (formData.preSessionEnabled) {
        const existingMaterials = formData.preSessionMaterials.filter(m => m.url && !m.file);
        const newMaterials = formData.preSessionMaterials.filter(m => m.file);
        const uploadedNew = [];
        for (const material of newMaterials) {
          try {
            const fd = new FormData();
            fd.append('file', material.file);
            const result = await api.upload(`/upload/session/${sessionId}/pre-read`, fd);
            if (result?.success && result.data) {
              uploadedNew.push(result.data);
            } else {
              console.error('[EditSession] pre-read upload unsuccessful response:', result);
            }
          } catch (err) {
            console.error('[EditSession] Error uploading pre-read material:', material.name, err);
          }
        }
        finalPreReadMaterials = [...existingMaterials, ...uploadedNew];
      }

      // Phase 2: build pre-session config (only if enabled — undefined otherwise
      // so backend leaves schema defaults / existing value untouched. But if the
      // toggle was explicitly turned off we DO want to persist enabled:false, so
      // we always send the object when the user visited Step 4 in edit mode.)
      const preSessionConfig = {
        enabled: formData.preSessionEnabled,
        materials: finalPreReadMaterials,
        selfAssessment: {
          enabled: formData.selfAssessmentEnabled,
          questions: formData.selfAssessmentQuestions.map((q, idx) => ({
            questionText: q.questionText,
            questionType: q.questionType,
            // Parse the raw `optionsText` at submit time so typing commas/spaces
            // mid-edit doesn't get eaten.
            options: q.optionsText !== undefined
              ? q.optionsText.split(',').map(s => s.trim()).filter(Boolean)
              : (q.options || []),
            order: idx,
          })),
        },
        challengeCapture: { enabled: formData.challengeCaptureEnabled },
        expectationCapture: { enabled: formData.expectationCaptureEnabled },
      };

      const postSessionConfig = {
        enabled: formData.postSessionEnabled,
        actionPlanTracking: { enabled: formData.actionPlanTrackingEnabled },
        managerReinforcement: { enabled: formData.managerReinforcementEnabled },
        reflectionCycles: {
          enabled: formData.reflectionCyclesEnabled,
          intervals: formData.reflectionIntervals,
          questions: formData.reflectionQuestions.map((q, idx) => ({
            questionText: q.questionText,
            order: idx,
          })),
        },
      };

      console.log('[EditSession] Sending time data:', {
        startDate: formData.startDate,
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone
      });

      // Build update data for API
      // Send raw time strings - backend will handle storage
      const updateData = {
        name: formData.name,
        description: formData.description,
        scheduledDate: formData.startDate,
        startDate: formData.startDate,
        endDate: formData.endDate || formData.startDate,
        // Send raw time strings (HH:MM format) - no timezone conversion
        startTime: formData.startTime,
        endTime: formData.endTime,
        timezone: formData.timezone,
        trainer: formData.trainerId,
        // Always use plan limit — field is read-only and user can't change it
        maxParticipants: isUnlimitedParticipants() ? 500 : getParticipantLimit(),
        expectedParticipants: formData.expectedParticipants,
        settings: {
          sessionType: formData.sessionType,
          instructions: formData.instructions,
        },
        workbooks: finalWorkbooks,
        // Overall session feedback settings
        overallFeedback: {
          enabled: formData.enableOverallFeedback,
        },
        // Phase 2: top-level siblings of settings (NOT nested inside it)
        preSession: preSessionConfig,
        postSession: postSessionConfig,
      };

      console.log('[EditSession] Sending update request...');
      const response = await sessionService.updateSession(sessionId, updateData);
      console.log('[EditSession] Update response:', response);

      if (response?.success) {
        notifications.sessionUpdated();
        navigate(`${basePath}/sessions/${sessionId}`);
      } else if (response?.data) {
        // Some APIs return data directly without success wrapper
        notifications.sessionUpdated();
        navigate(`${basePath}/sessions/${sessionId}`);
      } else {
        throw new Error(response?.error || response?.message || 'Failed to update session');
      }
    } catch (error) {
      console.error('[EditSession] Error updating session:', error);
      notifications.error(error.message || 'Failed to update session. Please try again.');
    } finally {
      setSaving(false);
    }
  };

  // Will be used for displaying trainer info in UI - reserved for future use
  // const selectedTrainer = trainers.find((t) => t.id === formData.trainerId);

  if (loading) {
    return (
      <DashboardLayout title="Edit Session">
        <div className="p-6 max-w-4xl mx-auto">
          <FormSkeleton />
        </div>
      </DashboardLayout>
    );
  }

  if (!session) {
    return (
      <DashboardLayout title="Edit Session">
        <div className="p-6 max-w-4xl mx-auto">
          <Card>
            <SessionNotFoundEmpty onBackToSessions={() => navigate(`${basePath}/sessions`)} />
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  // Can only edit scheduled or expired sessions (expired can be rescheduled)
  if (!['scheduled', 'expired'].includes(session.status)) {
    return (
      <DashboardLayout title="Edit Session">
        <div className="p-6 max-w-4xl mx-auto">
          <Card>
            <div className="p-12 text-center">
              <div className="inline-flex items-center justify-center w-16 h-16 bg-amber-100 rounded-full mb-4">
                <svg className="w-8 h-8 text-amber-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                  <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M12 9v2m0 4h.01m-6.938 4h13.856c1.54 0 2.502-1.667 1.732-3L13.732 4c-.77-1.333-2.694-1.333-3.464 0L3.34 16c-.77 1.333.192 3 1.732 3z" />
                </svg>
              </div>
              <h3 className="text-base sm:text-xl font-semibold text-neutral-900 mb-2">Cannot Edit {session.status === 'live' ? 'Live' : 'Completed'} Session</h3>
              <p className="text-neutral-600 mb-6">
                Only scheduled sessions can be edited. This session is currently {session.status}.
              </p>
              <div className="flex justify-center gap-3">
                <Button variant="ghost" onClick={() => navigate(`${basePath}/sessions`)}>
                  Back to Sessions
                </Button>
                <Button variant="primary" onClick={() => navigate(`${basePath}/sessions/${sessionId}`)}>
                  View Session
                </Button>
              </div>
            </div>
          </Card>
        </div>
      </DashboardLayout>
    );
  }

  return (
    <DashboardLayout title="Edit Session">
      <div className="w-full px-4 lg:px-6 py-6 max-w-5xl mx-auto pb-20 sm:pb-6 space-y-5">
        {/* Back link */}
        <button
          type="button"
          onClick={() => handleBackNavigation(`${basePath}/sessions`)}
          className="inline-flex items-center gap-1.5 text-sm text-neutral-500 hover:text-neutral-900 dark:text-neutral-400 dark:hover:text-neutral-100 transition-colors group"
        >
          <ArrowLeft size={14} className="transition-transform group-hover:-translate-x-0.5" />
          <span className="font-medium">Back to Sessions</span>
        </button>

        {/* Header */}
        <div className="flex items-start justify-between gap-3">
          <div className="space-y-1">
            <h1 className="text-2xl font-semibold tracking-tight text-neutral-900 dark:text-neutral-50">Edit Session</h1>
            <p className="text-sm text-neutral-500 dark:text-neutral-400">{session.name}</p>
          </div>
          <Button variant="ghost" size="sm" onClick={() => handleBackNavigation(`${basePath}/sessions/${sessionId}`)}>
            Cancel
          </Button>
        </div>

        {/* Progress Steps */}
        <div className="rounded-2xl bg-white dark:bg-neutral-900 ring-1 ring-neutral-200/70 dark:ring-neutral-800 shadow-[0_1px_2px_rgba(16,24,40,0.04)] p-5">
          <div className="flex items-center justify-between">
            {[1, 2, 3, 4].map((step) => {
              const done = step < currentStep;
              const active = step === currentStep;
              return (
                <div key={step} className="flex items-center flex-1 min-w-0">
                  <div className="flex items-center min-w-0">
                    <div
                      className={`w-10 h-10 rounded-full flex items-center justify-center font-semibold text-sm transition-all flex-shrink-0 ring-1 ring-inset ${
                        done
                          ? 'bg-emerald-500 text-white ring-emerald-500/40 shadow-[0_4px_12px_-4px_rgba(16,185,129,0.45)]'
                          : active
                          ? 'bg-primary-50 text-primary-700 ring-primary-200 dark:bg-primary-500/10 dark:text-primary-300 dark:ring-primary-500/30 shadow-[0_4px_12px_-4px_rgba(255,138,0,0.35)]'
                          : 'bg-neutral-50 text-neutral-500 ring-neutral-200 dark:bg-neutral-800 dark:text-neutral-500 dark:ring-neutral-700'
                      }`}
                    >
                      {done ? <Check size={16} strokeWidth={2.5} /> : step}
                    </div>
                    <div className="ml-3 hidden sm:block min-w-0">
                      <p className={`text-sm font-medium truncate ${
                        active
                          ? 'text-neutral-900 dark:text-neutral-100'
                          : done
                          ? 'text-neutral-700 dark:text-neutral-300'
                          : 'text-neutral-500 dark:text-neutral-500'
                      }`}>
                        {step === 1 ? 'Basic Info' : step === 2 ? 'Trainer & Participants' : step === 3 ? 'Workbooks' : 'Pre & Post Session'}
                      </p>
                    </div>
                  </div>
                  {step < 4 && (
                    <div className={`flex-1 h-0.5 mx-3 sm:mx-4 rounded-full ${
                      done ? 'bg-emerald-500' : 'bg-neutral-200 dark:bg-neutral-800'
                    }`} />
                  )}
                </div>
              );
            })}
          </div>
        </div>

        {/* Step Content */}
        <AnimatePresence mode="wait">
          {/* Step 1: Basic Information */}
          {currentStep === 1 && (
            <motion.div
              key="step1"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">Basic Information</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Update the basic details about your training session</p>
                  </div>

                  <Input
                    label="Session Name"
                    name="name"
                    value={formData.name}
                    onChange={handleChange}
                    error={errors.name}
                    placeholder="Leadership Training - Day 1"
                    required
                    fullWidth
                  />

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Description</label>
                    <textarea
                      name="description"
                      value={formData.description}
                      onChange={handleChange}
                      placeholder="A comprehensive training on leadership principles..."
                      rows={4}
                      className="w-full px-4 py-2.5 text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all resize-none"
                    />
                  </div>

                  {/* Start Date and End Date */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    <Input
                      label="Start Date"
                      name="startDate"
                      type="date"
                      value={formData.startDate}
                      onChange={handleChange}
                      error={errors.startDate}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label={<>End Date <span className="text-neutral-400 text-xs font-normal">(optional)</span></>}
                        name="endDate"
                        type="date"
                        value={formData.endDate}
                        onChange={handleChange}
                        error={errors.endDate}
                        fullWidth
                        min={formData.startDate}
                      />
                      {!formData.endDate && formData.startDate && (
                        <p className="text-xs text-neutral-500 mt-1">Defaults to start date if not set</p>
                      )}
                    </div>
                  </div>

                  {/* Custom Time Picker with AM/PM - manual entry with validation */}
                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4 sm:gap-6">
                    {/* Start Time */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        Start Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={timeData.startHour}
                          onChange={(e) => handleTimeChange('startHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={timeData.startMinute}
                          onChange={(e) => handleTimeChange('startMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('startMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.startPeriod}
                          onChange={(e) => handleTimeChange('startPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.startTime && (
                        <p className="text-xs text-error mt-1">{errors.startTime}</p>
                      )}
                    </div>

                    {/* End Time */}
                    <div>
                      <label className="block text-sm font-medium text-neutral-700 mb-2">
                        End Time <span className="text-neutral-400 text-xs font-normal">(optional)</span>
                      </label>
                      <div className="flex items-center gap-2">
                        <input
                          type="number"
                          min="1"
                          max="12"
                          value={timeData.endHour}
                          onChange={(e) => handleTimeChange('endHour', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endHour', e.target.value)}
                          placeholder="HH"
                          className="w-16 px-2 py-2.5 text-sm text-center text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <span className="text-neutral-400 font-medium">:</span>
                        <input
                          type="number"
                          min="0"
                          max="59"
                          value={timeData.endMinute}
                          onChange={(e) => handleTimeChange('endMinute', e.target.value)}
                          onBlur={(e) => handleTimeBlur('endMinute', e.target.value)}
                          placeholder="MM"
                          className="w-16 px-2 py-2.5 text-sm text-center text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all [appearance:textfield] [&::-webkit-outer-spin-button]:appearance-none [&::-webkit-inner-spin-button]:appearance-none"
                        />
                        <select
                          value={timeData.endPeriod}
                          onChange={(e) => handleTimeChange('endPeriod', e.target.value)}
                          className="px-3 py-2.5 pr-8 text-sm font-medium text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all appearance-none cursor-pointer"
                          style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.5rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                        >
                          <option value="AM">AM</option>
                          <option value="PM">PM</option>
                        </select>
                      </div>
                      {errors.endTime && (
                        <p className="text-xs text-error mt-1">{errors.endTime}</p>
                      )}
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Time Zone</label>
                    <select
                      name="timezone"
                      value={formData.timezone}
                      onChange={handleChange}
                      className="w-full px-4 py-2.5 pr-10 text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all appearance-none"
                      style={{ backgroundImage: `url("data:image/svg+xml,%3csvg xmlns='http://www.w3.org/2000/svg' fill='none' viewBox='0 0 20 20'%3e%3cpath stroke='%236b7280' stroke-linecap='round' stroke-linejoin='round' stroke-width='1.5' d='M6 8l4 4 4-4'/%3e%3c/svg%3e")`, backgroundPosition: 'right 0.75rem center', backgroundRepeat: 'no-repeat', backgroundSize: '1.25em 1.25em' }}
                    >
                      <option value="Asia/Kolkata">IST (UTC+05:30) India</option>
                    </select>
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={() => handleBackNavigation(`${basePath}/sessions/${sessionId}`)}>
                      Cancel
                    </Button>
                    <Button variant="primary" size="lg" onClick={handleNext}>
                      Next: Trainer →
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 2: Trainer & Participants */}
          {currentStep === 2 && (
            <motion.div
              key="step2"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">Trainer & Participants</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Update trainer assignment and participant capacity</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-2">Assign Trainer *</label>
                    {errors.trainerId && <p className="text-xs text-error mb-2">{errors.trainerId}</p>}
                    <select
                      name="trainerId"
                      value={formData.trainerId}
                      onChange={handleChange}
                      className={`w-full px-4 py-3 border rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-500 transition-all ${
                        errors.trainerId ? 'border-error' : 'border-neutral-300'
                      }`}
                    >
                      <option value="">Select a trainer</option>
                      {trainers.map((trainer) => (
                        <option key={trainer.id} value={trainer.id}>
                          {trainer.fullName} - {trainer.expertise.join(', ')} ({trainer.totalSessions} sessions)
                        </option>
                      ))}
                    </select>
                    {formData.trainerId && (
                      <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                        {(() => {
                          const selectedTrainer = trainers.find((t) => t.id === formData.trainerId);
                          if (!selectedTrainer) return null;
                          return (
                            <div className="flex items-center gap-3">
                              <div className="w-10 h-10 bg-primary-600 rounded-full flex items-center justify-center text-white font-semibold">
                                {selectedTrainer.fullName.charAt(0)}
                              </div>
                              <div>
                                <p className="font-semibold text-neutral-900">{selectedTrainer.fullName}</p>
                                <p className="text-xs sm:text-sm text-neutral-600">{selectedTrainer.expertise.join(', ')}</p>
                                <div className="flex items-center gap-3 mt-1">
                                  <span className="text-xs text-neutral-500">{selectedTrainer.yearsOfExperience} years exp</span>
                                  <span className="text-xs text-neutral-500">★ {selectedTrainer.avgRating}/5</span>
                                  <span className="text-xs text-neutral-500">{selectedTrainer.totalSessions} sessions</span>
                                </div>
                              </div>
                            </div>
                          );
                        })()}
                      </div>
                    )}
                  </div>

                  <div className="grid grid-cols-1 sm:grid-cols-2 gap-4">
                    <Input
                      label="Expected Participants"
                      name="expectedParticipants"
                      type="number"
                      value={formData.expectedParticipants}
                      onChange={handleChange}
                      error={errors.expectedParticipants}
                      min={1}
                      max={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                      required
                      fullWidth
                    />
                    <div>
                      <Input
                        label="Maximum Capacity"
                        name="maxParticipants"
                        type="number"
                        value={isUnlimitedParticipants() ? 500 : getParticipantLimit()}
                        readOnly
                        disabled
                        fullWidth
                      />
                      <p className="text-xs text-neutral-500 mt-1">
                        {isUnlimitedParticipants() ? 'Unlimited as per your plan' : `Fixed at ${getParticipantLimit()} as per your plan`}
                      </p>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-3">Session Type</label>
                    <div className="space-y-2">
                      <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors">
                        <input
                          type="radio"
                          name="sessionType"
                          value="public"
                          checked={formData.sessionType === 'public'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600"
                        />
                        <div className="ml-3">
                          <p className="font-medium text-neutral-900">Public</p>
                          <p className="text-xs sm:text-sm text-neutral-600">Anyone with code can join</p>
                        </div>
                      </label>
                      <label className="flex items-center p-3 border-2 rounded-lg cursor-pointer hover:bg-neutral-50 transition-colors">
                        <input
                          type="radio"
                          name="sessionType"
                          value="private"
                          checked={formData.sessionType === 'private'}
                          onChange={handleChange}
                          className="w-4 h-4 text-primary-600"
                        />
                        <div className="ml-3">
                          <p className="font-medium text-neutral-900">Private</p>
                          <p className="text-xs sm:text-sm text-neutral-600">Pre-registered participants only</p>
                        </div>
                      </label>
                    </div>
                  </div>

                  {/* Overall Session Feedback Toggle */}
                  <div className="pt-4 border-t border-neutral-200">
                    <div className="flex items-center justify-between">
                      <div className="flex-1">
                        <label className="block text-sm font-medium text-neutral-900">Overall Session Feedback</label>
                        <p className="text-xs text-neutral-500 mt-0.5">
                          Collect feedback at the end of the entire session (after all subsessions)
                        </p>
                      </div>
                      <button
                        type="button"
                        onClick={() => {
                          setFormData(prev => ({ ...prev, enableOverallFeedback: !prev.enableOverallFeedback }));
                          setIsDirty(true);
                        }}
                        className={`relative inline-flex h-6 w-11 items-center rounded-full transition-colors focus:outline-none focus:ring-2 focus:ring-primary-500 focus:ring-offset-2 ${
                          formData.enableOverallFeedback ? 'bg-primary-600' : 'bg-neutral-300'
                        }`}
                      >
                        <span
                          className={`inline-block h-4 w-4 transform rounded-full bg-white transition-transform ${
                            formData.enableOverallFeedback ? 'translate-x-6' : 'translate-x-1'
                          }`}
                        />
                      </button>
                    </div>
                    {formData.enableOverallFeedback && (
                      <div className="mt-3 p-3 bg-primary-50 border border-primary-200 rounded-lg">
                        <div className="flex items-start gap-2">
                          <svg className="w-5 h-5 text-primary-600 flex-shrink-0 mt-0.5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                            <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                          </svg>
                          <p className="text-xs sm:text-sm text-primary-800">
                            Participants will be asked to rate the overall session and provide feedback after all subsessions are completed. This will be shown in session reports.
                          </p>
                        </div>
                      </div>
                    )}
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={handleBack}>
                      ← Back
                    </Button>
                    <Button variant="primary" size="lg" onClick={handleNext}>
                      Next: Workbooks →
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 3: Workbooks & Materials */}
          {currentStep === 3 && (
            <motion.div
              key="step3"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
            >
              <Card>
                <div className="p-6 space-y-6">
                  <div>
                    <h2 className="text-base sm:text-xl font-bold text-neutral-900 mb-2">Workbooks & Materials</h2>
                    <p className="text-xs sm:text-sm text-neutral-600">Update session materials and instructions</p>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-3">Upload Workbooks</label>

                    {/* File Type Notice */}
                    <div className="flex items-center gap-2 p-3 bg-amber-50 border border-amber-200 rounded-lg mb-4">
                      <svg className="w-5 h-5 text-amber-600 flex-shrink-0" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                        <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M13 16h-1v-4h-1m1-4h.01M21 12a9 9 0 11-18 0 9 9 0 0118 0z" />
                      </svg>
                      <p className="text-xs sm:text-sm text-amber-800">
                        <span className="font-medium">Accepted formats:</span> PDF, PPT, PPTX only
                        {' · '}
                        <span className="font-medium">Max size:</span> {MAX_UPLOAD_MB} MB per file
                      </p>
                    </div>

                    <div className="border-2 border-dashed border-neutral-300 rounded-lg p-6">
                      {formData.workbooks.length > 0 && (
                        <div className="space-y-2 mb-4">
                          {formData.workbooks.map((file) => (
                            <div
                              key={file.id}
                              className="flex items-center justify-between p-3 bg-neutral-50 rounded-lg"
                            >
                              <div className="flex items-center gap-3">
                                <svg className="w-8 h-8 text-primary-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                                  <path
                                    strokeLinecap="round"
                                    strokeLinejoin="round"
                                    strokeWidth={2}
                                    d="M7 21h10a2 2 0 002-2V9.414a1 1 0 00-.293-.707l-5.414-5.414A1 1 0 0012.586 3H7a2 2 0 00-2 2v14a2 2 0 002 2z"
                                  />
                                </svg>
                                <div>
                                  <p className="font-medium text-neutral-900">{file.name}</p>
                                  <p className="text-xs text-neutral-500">
                                    {file.size ? `${(file.size / 1024 / 1024).toFixed(2)} MB` : 'Uploaded'}
                                  </p>
                                </div>
                              </div>
                              <button
                                onClick={() => removeWorkbook(file.id)}
                                className="text-error hover:text-red-700 font-medium text-xs sm:text-sm"
                              >
                                Remove
                              </button>
                            </div>
                          ))}
                        </div>
                      )}

                      <div className="text-center">
                        <label className="cursor-pointer">
                          <input
                            type="file"
                            multiple
                            accept=".pdf,.ppt,.pptx"
                            onChange={handleFileUpload}
                            className="hidden"
                            disabled={formData.workbooks.length >= ({ free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.organization?.plan || user?.subscriptionTier] || 2)}
                          />
                          <div className="inline-flex items-center gap-2 px-4 py-2 bg-primary-600 text-white rounded-lg hover:bg-primary-700 transition-colors">
                            <svg className="w-5 h-5" fill="none" stroke="currentColor" viewBox="0 0 24 24">
                              <path
                                strokeLinecap="round"
                                strokeLinejoin="round"
                                strokeWidth={2}
                                d="M7 16a4 4 0 01-.88-7.903A5 5 0 1115.9 6L16 6a5 5 0 011 9.9M15 13l-3-3m0 0l-3 3m3-3v12"
                              />
                            </svg>
                            Upload Files
                          </div>
                        </label>
                        <p className="text-xs text-neutral-500 mt-2">
                          Files: {formData.workbooks.length}/{({ free: 2, pro: 50, pro_plus: 50, enterprise_starter: 50, enterprise_scale: 50 }[user?.organization?.plan || user?.subscriptionTier] || 2)}{' '}
                          ({user?.organization?.plan || 'Free'} Plan)
                        </p>
                      </div>
                    </div>
                  </div>

                  <div>
                    <label className="block text-sm font-medium text-neutral-700 mb-1.5">Session Instructions (Optional)</label>
                    <textarea
                      name="instructions"
                      value={formData.instructions}
                      onChange={handleChange}
                      placeholder="Please download the materials before the session..."
                      rows={4}
                      className="w-full px-4 py-2.5 text-neutral-900 bg-white border border-neutral-300 rounded-lg focus:outline-none focus:ring-2 focus:ring-primary-100 focus:border-primary-600 transition-all resize-none"
                    />
                  </div>

                  <div className="flex justify-between pt-4">
                    <Button variant="ghost" onClick={handleBack}>
                      ← Back
                    </Button>
                    <Button variant="primary" size="lg" onClick={handleNext}>
                      Next: Pre & Post Session →
                    </Button>
                  </div>
                </div>
              </Card>
            </motion.div>
          )}

          {/* Step 4: Pre & Post Session (Phase 2) */}
          {currentStep === 4 && (
            <motion.div
              key="step4"
              initial={{ opacity: 0, x: 20 }}
              animate={{ opacity: 1, x: 0 }}
              exit={{ opacity: 0, x: -20 }}
              transition={{ duration: 0.3 }}
              className="space-y-5"
            >
              {/* Header (inline, no card) */}
              <div>
                <h2 className="text-lg sm:text-xl font-semibold text-neutral-900 mb-1">
                  Pre & Post Session Features
                </h2>
                <p className="text-sm text-neutral-600">
                  Optional features to enhance learning before and after training. Skip this step if not needed.
                </p>
              </div>

              {/* Pre-Session card (blue tint) */}
              <div className="bg-blue-50 border border-blue-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Enable Pre-Session</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants complete tasks before the training day
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.preSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, preSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.preSessionEnabled && (
                  <div className="pt-4 border-t border-blue-200 space-y-4">
                      {/* Materials upload */}
                      <div>
                        <label className="block text-sm font-medium text-neutral-900 mb-2">Pre-Read Materials</label>
                        <input
                          type="file"
                          multiple
                          accept=".pdf,.ppt,.pptx,.doc,.docx,image/*,video/*"
                          onChange={(e) => {
                            const files = Array.from(e.target.files).map(f => ({
                              file: f,
                              name: f.name,
                              size: f.size,
                              type: f.type,
                            }));
                            setFormData({
                              ...formData,
                              preSessionMaterials: [...formData.preSessionMaterials, ...files],
                            });
                            e.target.value = '';
                          }}
                          className="block w-full text-sm text-neutral-700 file:mr-3 file:py-2 file:px-4 file:rounded-md file:border-0 file:text-sm file:bg-primary-50 file:text-primary-700 hover:file:bg-primary-100"
                        />
                        {formData.preSessionMaterials.length > 0 && (
                          <ul className="mt-2 space-y-1">
                            {formData.preSessionMaterials.map((m, idx) => (
                              <li key={idx} className="flex items-center justify-between text-sm bg-neutral-50 px-3 py-2 rounded">
                                <span className="truncate">
                                  {m.name}
                                  {m.url && !m.file && <span className="ml-2 text-xs text-neutral-500">(saved)</span>}
                                </span>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    preSessionMaterials: formData.preSessionMaterials.filter((_, i) => i !== idx),
                                  })}
                                  className="text-red-500 hover:text-red-700 ml-2"
                                >
                                  ✕
                                </button>
                              </li>
                            ))}
                          </ul>
                        )}
                      </div>

                      {/* Self-assessment toggle */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Self-Assessment</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.selfAssessmentEnabled}
                            onChange={(e) => setFormData({ ...formData, selfAssessmentEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {formData.selfAssessmentEnabled && (
                        <div className="pl-4 border-l-2 border-neutral-100 space-y-3">
                          {formData.selfAssessmentQuestions.map((q, idx) => (
                            <div key={idx} className="bg-neutral-50 p-3 rounded space-y-2">
                              <div className="flex items-center gap-2">
                                <input
                                  type="text"
                                  placeholder="Question text"
                                  value={q.questionText}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, questionText: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm"
                                />
                                <select
                                  value={q.questionType}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, questionType: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="px-3 py-2 border border-neutral-200 rounded text-sm"
                                >
                                  <option value="rating">Rating (1-5)</option>
                                  <option value="text">Text</option>
                                  <option value="mcq">Multiple Choice</option>
                                </select>
                                <button
                                  type="button"
                                  onClick={() => setFormData({
                                    ...formData,
                                    selfAssessmentQuestions: formData.selfAssessmentQuestions.filter((_, i) => i !== idx),
                                  })}
                                  className="text-red-500 hover:text-red-700"
                                >
                                  ✕
                                </button>
                              </div>
                              {q.questionType === 'mcq' && (
                                <input
                                  type="text"
                                  placeholder="Options (comma-separated)"
                                  // Keep the raw input in `optionsText` — splitting/trimming on
                                  // every keystroke eats commas and spaces mid-type. Parsed into
                                  // the final `options` array at submit time.
                                  value={q.optionsText !== undefined ? q.optionsText : (q.options || []).join(', ')}
                                  onChange={(e) => {
                                    const updated = [...formData.selfAssessmentQuestions];
                                    updated[idx] = { ...q, optionsText: e.target.value };
                                    setFormData({ ...formData, selfAssessmentQuestions: updated });
                                  }}
                                  className="w-full px-3 py-2 border border-neutral-200 rounded text-sm"
                                />
                              )}
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              selfAssessmentQuestions: [
                                ...formData.selfAssessmentQuestions,
                                { questionText: '', questionType: 'rating', options: [], optionsText: '' },
                              ],
                            })}
                          >
                            + Add Question
                          </Button>
                        </div>
                      )}

                      {/* Challenge capture */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Challenge Capture</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.challengeCaptureEnabled}
                            onChange={(e) => setFormData({ ...formData, challengeCaptureEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Expectation capture */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Expectation Capture</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.expectationCaptureEnabled}
                            onChange={(e) => setFormData({ ...formData, expectationCaptureEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>
                  </div>
                )}
              </div>

              {/* Post-Session card (green tint) */}
              <div className="bg-green-50 border border-green-100 rounded-xl p-5 sm:p-6 space-y-4">
                <div className="flex items-start justify-between gap-4">
                  <div className="flex-1">
                    <h3 className="text-base sm:text-lg font-semibold text-neutral-900">Enable Post-Session Tracking</h3>
                    <p className="text-sm text-neutral-600 mt-1">
                      Participants track action plans and reflect after training
                    </p>
                  </div>
                  <label className="relative inline-flex items-center cursor-pointer flex-shrink-0 mt-1">
                    <input
                      type="checkbox"
                      className="sr-only peer"
                      checked={formData.postSessionEnabled}
                      onChange={(e) => setFormData({ ...formData, postSessionEnabled: e.target.checked })}
                    />
                    <div className="w-11 h-6 bg-neutral-300 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:border-neutral-300 after:border after:rounded-full after:h-5 after:w-5 after:transition-all peer-checked:bg-primary-600"></div>
                  </label>
                </div>

                {formData.postSessionEnabled && (
                  <div className="pt-4 border-t border-green-200 space-y-4">
                      {/* Action plan tracking */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Action Plan Tracking</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.actionPlanTrackingEnabled}
                            onChange={(e) => setFormData({ ...formData, actionPlanTrackingEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Manager reinforcement */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Manager Reinforcement</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.managerReinforcementEnabled}
                            onChange={(e) => setFormData({ ...formData, managerReinforcementEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                      {/* Reflection cycles */}
                      <div className="flex items-center justify-between">
                        <label className="text-sm font-medium text-neutral-900">Reflection Cycles</label>
                        <label className="relative inline-flex items-center cursor-pointer">
                          <input
                            type="checkbox"
                            className="sr-only peer"
                            checked={formData.reflectionCyclesEnabled}
                            onChange={(e) => setFormData({ ...formData, reflectionCyclesEnabled: e.target.checked })}
                          />
                          <div className="w-9 h-5 bg-neutral-200 rounded-full peer peer-checked:after:translate-x-full after:content-[''] after:absolute after:top-[2px] after:left-[2px] after:bg-white after:rounded-full after:h-4 after:w-4 after:transition-all peer-checked:bg-primary-600"></div>
                        </label>
                      </div>

                    {formData.reflectionCyclesEnabled && (
                      <div className="pl-4 border-l-2 border-green-200 space-y-3">
                        <div>
                          <label className="block text-sm font-medium text-neutral-900 mb-1">
                            Reflection Intervals (days after session)
                          </label>
                          <input
                            type="text"
                            value={reflectionIntervalsText}
                            onChange={(e) => {
                              // Show exactly what was typed; parse alongside it rather
                              // than feeding the parsed value back into the field.
                              setReflectionIntervalsText(e.target.value);
                              const intervals = e.target.value
                                .split(',')
                                .map(s => parseInt(s.trim(), 10))
                                .filter(n => Number.isFinite(n) && n > 0);
                              setFormData({ ...formData, reflectionIntervals: intervals });
                            }}
                            // Tidy up once they leave the field: drops stray separators
                            // and whitespace without interfering mid-entry.
                            onBlur={() => setReflectionIntervalsText(formData.reflectionIntervals.join(', '))}
                            placeholder="30, 60, 90"
                            className="w-full px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                          />
                          <p className="text-xs text-neutral-500 mt-1">Comma-separated numbers. Default: 30, 60, 90.</p>
                        </div>

                        <div>
                          <label className="block text-sm font-medium text-neutral-900 mb-2">
                            Reflection Questions
                          </label>
                          {formData.reflectionQuestions.map((q, idx) => (
                            <div key={idx} className="flex items-center gap-2 mb-2">
                              <input
                                type="text"
                                value={q.questionText}
                                onChange={(e) => {
                                  const updated = [...formData.reflectionQuestions];
                                  updated[idx] = { ...q, questionText: e.target.value };
                                  setFormData({ ...formData, reflectionQuestions: updated });
                                }}
                                className="flex-1 px-3 py-2 border border-neutral-200 rounded text-sm bg-white"
                              />
                              <button
                                type="button"
                                onClick={() => setFormData({
                                  ...formData,
                                  reflectionQuestions: formData.reflectionQuestions.filter((_, i) => i !== idx),
                                })}
                                className="text-red-500 hover:text-red-700"
                              >
                                ✕
                              </button>
                            </div>
                          ))}
                          <Button
                            type="button"
                            variant="outline"
                            size="sm"
                            onClick={() => setFormData({
                              ...formData,
                              reflectionQuestions: [
                                ...formData.reflectionQuestions,
                                { questionText: '', order: formData.reflectionQuestions.length },
                              ],
                            })}
                          >
                            + Add Question
                          </Button>
                        </div>
                      </div>
                    )}
                  </div>
                )}
              </div>

              {/* Info note */}
              <div className="bg-neutral-50 border border-neutral-200 rounded-lg px-5 py-4 text-center">
                <p className="text-sm text-neutral-600">
                  Pre & Post session features are optional. You can skip this step and the session will work exactly as before.
                </p>
              </div>

              {/* Navigation buttons */}
              <div className="flex justify-between pt-2">
                <Button variant="ghost" onClick={handleBack}>
                  ← Back
                </Button>
                <Button variant="primary" size="lg" onClick={handleSaveSession} disabled={saving}>
                  {saving ? 'Saving...' : 'Save Changes'}
                </Button>
              </div>
            </motion.div>
          )}
        </AnimatePresence>
      </div>

      {/* Unsaved Changes Confirmation Dialog */}
      <ConfirmDialog
        isOpen={showBackConfirm}
        onClose={() => setShowBackConfirm(false)}
        onConfirm={confirmLeave}
        title="Unsaved Changes"
        message="You have unsaved changes. Are you sure you want to leave without saving?"
        confirmText="Yes, Leave"
        cancelText="No, Stay"
        variant="warning"
      />
    </DashboardLayout>
  );
};

export default EditSessionPage;
